import { paymentApi } from './paymentApi';

export type SubscriptionStatus = 'trial' | 'active' | 'expired' | 'cancelled';
export type PlanType = 'basic' | 'medium' | 'advanced' | 'monthly' | null;
export type PaymentType = 'one-time' | 'recurring' | 'manual';

export interface PlanConfig {
  price: number;
  limit: number;
  name: string;
}

export const PLAN_DEFAULTS: Record<string, PlanConfig> = {
  basic: { price: 29.90, limit: 5, name: 'Básico' },
  medium: { price: 59.90, limit: 20, name: 'Médio' },
  advanced: { price: 99.90, limit: 9999, name: 'Avançado' },
  monthly: { price: 99.90, limit: 9999, name: 'Mensal (Legacy)' },
};

export interface Subscription {
  status: SubscriptionStatus;
  trialStartDate: string;
  trialEndDate: string;
  subscriptionStartDate?: string | null;
  subscriptionEndDate?: string | null;
  planType: PlanType;
  accessSource?: 'trial' | 'mercadopago' | 'admin';
  patientLimit?: number;
  paymentType?: PaymentType;
  mercadoPagoSubscriptionId?: string | null;
  mercadoPagoPreferenceId?: string | null;
  lastPaymentDate?: string | null;
  nextPaymentDate?: string | null;
  cancelledAt?: string | null;
  accessEndsAt?: string | null;
}

/** Trial e status são mantidos pelo servidor PHP, nunca pelo cliente. */
export async function initializeTrial(userId: string): Promise<void> {
  if (!userId) throw new Error('User ID é obrigatório');
  await paymentApi<Subscription>('subscription');
}

export async function checkSubscriptionStatus(userId: string): Promise<Subscription | null> {
  if (!userId) return null;
  return paymentApi<Subscription>('subscription');
}

export async function isSubscriptionActive(userId: string): Promise<boolean> {
  const subscription = await checkSubscriptionStatus(userId);
  return subscription?.status === 'trial' || subscription?.status === 'active';
}

export async function getDaysRemainingInTrial(userId: string): Promise<number> {
  const subscription = await checkSubscriptionStatus(userId);
  if (!subscription || subscription.status !== 'trial' || !subscription.trialEndDate) return 0;
  const remaining = Math.ceil((new Date(subscription.trialEndDate).getTime() - Date.now()) / 86400000);
  return Math.max(0, remaining);
}

/** Ativação de plano acontece somente após confirmação no servidor ou no painel Super Admin. */
export async function activateSubscription(
  _userId: string,
  _data: {
    mercadoPagoSubscriptionId?: string;
    mercadoPagoPreferenceId?: string;
    planType: PlanType;
    paymentType: PaymentType;
  }
): Promise<void> {
  throw new Error('A assinatura é liberada pelo servidor após confirmação de pagamento ou pelo Super Admin.');
}

export async function renewSubscription(_userId: string): Promise<void> {
  throw new Error('Renovações são processadas exclusivamente pelo servidor.');
}

export async function cancelSubscription(userId: string): Promise<void> {
  if (!userId) throw new Error('User ID é obrigatório');
  await paymentApi('cancel', { method: 'POST' });
}

export async function hasActiveAccess(userId: string): Promise<boolean> {
  const subscription = await checkSubscriptionStatus(userId);
  if (!subscription) return false;
  if (subscription.status === 'trial' || subscription.status === 'active') return true;
  return subscription.status === 'cancelled'
    && !!subscription.accessEndsAt
    && Date.now() <= new Date(subscription.accessEndsAt).getTime();
}

export async function getPlans(): Promise<Record<string, PlanConfig>> {
  try {
    const plans = await paymentApi<Record<string, PlanConfig>>('plans', { authenticated: false });
    return { ...PLAN_DEFAULTS, ...plans };
  } catch (error) {
    console.error('Não foi possível carregar os planos MySQL:', error);
    return PLAN_DEFAULTS;
  }
}

/** A API Hostinger fornece a lista atual; atualização automática é feita ao abrir a tela. */
export function subscribeToPlans(callback: (plans: Record<string, PlanConfig>) => void): () => void {
  let active = true;
  getPlans().then(plans => { if (active) callback(plans); });
  return () => { active = false; };
}

export async function updatePlan(planId: string, data: Partial<PlanConfig>): Promise<void> {
  if (!planId) return;
  await paymentApi('update-plan', {
    method: 'POST',
    body: { planType: planId, name: data.name, price: data.price, limit: data.limit },
  });
}

export async function getMaxClients(userId: string): Promise<number> {
  const subscription = await checkSubscriptionStatus(userId);
  const cancelledAccess = subscription?.status === 'cancelled'
    && !!subscription.accessEndsAt
    && Date.now() <= new Date(subscription.accessEndsAt).getTime();
  if (!subscription || (!['active', 'trial'].includes(subscription.status) && !cancelledAccess)) return 0;
  if (subscription.status === 'trial') return PLAN_DEFAULTS.advanced.limit;
  if (Number.isInteger(subscription.patientLimit)) return Math.max(0, subscription.patientLimit as number);
  const plan = subscription.planType;
  if (!plan) return 0;
  const plans = await getPlans();
  return plans[plan]?.limit ?? 0;
}
